import { useMemo } from 'react';
import { Button, Skeleton, Text } from '@mantine/core';
import { generatePath, Link } from 'react-router-dom';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDetailsEmptyCardState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.tsx';
import { EMPTY_CARD_STATE_HEIGHT } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsEmptyCardState/InvestorDetailsEmptyCardState.styles.tsx';
import { InvestorLoanRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/LoansCard/investorLoans.types.ts';
import { getSingleFunderId } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/LoansCard/investorLoans.utils.ts';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations.ts';
import { getInvestorLoansTableColumns } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/LoansCard/investorLoansTable.utils.tsx';

interface Props {
	dealId: string;
	rows: InvestorLoanRow[];
	isLoading: boolean;
	isError: boolean;
	showIssuer?: boolean;
}

export const LoansSection = ({ dealId, rows, isLoading, isError, showIssuer }: Props) => {
	const tMipManage = useTranslationMipManage();
	const getLoanTypeLabel = useSecurityTypeTranslation();
	const dataOperations = useMantineTableDataOperations();

	const columns = useMemo(
		() => getInvestorLoansTableColumns({ tMipManage, getLoanTypeLabel, showIssuer }),
		[tMipManage, getLoanTypeLabel, showIssuer]
	);

	const singleFunderId = useMemo(() => getSingleFunderId(rows), [rows]);

	const captablePath = singleFunderId
		? generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.HOLDINGS}`, {
				dealId,
				personId: singleFunderId,
			})
		: null;

	const renderContent = () => {
		if (isLoading) {
			return <Skeleton height={EMPTY_CARD_STATE_HEIGHT} radius="0.5rem" />;
		}

		if (isError) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-piled-disks"
					title={tMipManage('investors.details.loans_table.error_title')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_LOANS_ERROR}
				/>
			);
		}

		if (!rows.length) {
			return (
				<InvestorDetailsEmptyCardState
					icon="es-piled-disks"
					title={tMipManage('investors.details.loans_table.empty_title')}
					description={tMipManage('investors.details.loans_table.empty_description')}
					dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_LOANS_EMPTY}
				/>
			);
		}

		return (
			<MantineTable
				columns={columns}
				data={rows}
				dataOperations={dataOperations}
				manualFiltering={false}
				manualSorting={false}
				enablePagination={false}
				enableTopToolbar={false}
				dataTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_LOANS_TABLE}
			/>
		);
	};

	return (
		<div data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_LOANS_CARD}>
			<InvestmentDetailsCard
				cardLabelComponent={
					<Text fw={600} c="dark.8" className="label">
						{tMipManage('investors.details.loans_table.title')}
					</Text>
				}
				actions={
					captablePath ? (
						<Button
							component={Link}
							to={captablePath}
							variant="default"
							size="md"
							color="dark.8"
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_LOANS_OPEN_CAPTABLE
							}
						>
							{tMipManage('investors.details.loans_table.open_captable')}
						</Button>
					) : undefined
				}
				variant="borderless"
			>
				{renderContent()}
			</InvestmentDetailsCard>
		</div>
	);
};
